"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AudioVisualizer } from "./AudioVisualizer";
import { TranscriptDisplay } from "./TranscriptDisplay";
import { SessionAnalysis, type AnalysisResult } from "./SessionAnalysis";
import {
  SessionOnboarding,
  onboardingStorageKey,
} from "./SessionOnboarding";
import {
  GeminiSession,
  type SessionState,
  type TranscriptEntry,
} from "@/lib/geminiSession";
import { getBackendHttpBase } from "@/lib/backendBase";
import { saveTrainingSession } from "@/lib/sessionsApi";

function transcriptStorageKey(agentId: string) {
  return `farmavoice_last_transcript_${agentId}`;
}

const MR_NAME_STORAGE_KEY = "farmavoice_mr_name";

interface AgentProfile {
  name: string;
  role: string;
  character: string;
  objection_level: string;
}

interface VoiceInterfaceProps {
  agentId: string;
  profile: AgentProfile;
  productName?: string;
  maxSessionMinutes?: number;
}

const STATE_LABELS: Record<SessionState, string> = {
  idle: "Pripravljeno",
  loading_docs: "Nalagam dokumente…",
  connecting: "Vzpostavljam zvezo…",
  live: "Poslušam…",
  ai_speaking: "Agent govori…",
  reconnecting: "Ponovno povezujem…",
  error: "Napaka",
  ended: "Seja končana",
};

const STATE_COLORS: Record<SessionState, string> = {
  idle: "text-slate-400",
  loading_docs: "text-amber-400",
  connecting: "text-amber-400",
  live: "text-emerald-400",
  ai_speaking: "text-blue-400",
  reconnecting: "text-amber-400",
  error: "text-red-400",
  ended: "text-slate-500",
};

const OBJECTION_BADGE: Record<string, { label: string; cls: string }> = {
  low: { label: "Nizka", cls: "bg-emerald-900/60 text-emerald-300" },
  medium: { label: "Srednja", cls: "bg-amber-900/60 text-amber-300" },
  high: { label: "Visoka", cls: "bg-red-900/60 text-red-300" },
};

export function VoiceInterface({
  agentId,
  profile,
  productName,
  maxSessionMinutes = 30,
}: VoiceInterfaceProps) {
  const sessionRef = useRef<GeminiSession | null>(null);
  const [sessionState, setSessionState] = useState<SessionState>("idle");
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [inputRms, setInputRms] = useState(0);
  const [outputRms, setOutputRms] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stoppedByTimerRef = useRef(false);
  const lastTranscriptMergeRef = useRef<{ role: string; atMs: number } | null>(
    null,
  );
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const transcriptRef = useRef<TranscriptEntry[]>([]);
  const sessionStartedAtRef = useRef<number | null>(null);
  const sessionDurationRef = useRef(0);
  const [hasSavedTranscript, setHasSavedTranscript] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [sessionJustEnded, setSessionJustEnded] = useState(false);
  const analysisRef = useRef<HTMLDivElement>(null);
  const [mrName, setMrName] = useState("");
  const [sessionSaveNote, setSessionSaveNote] = useState<string | null>(null);

  const isActive =
    sessionState !== "idle" &&
    sessionState !== "ended" &&
    sessionState !== "error";

  const isAiSpeaking = sessionState === "ai_speaking";
  const isListening = sessionState === "live";

  useEffect(() => {
    sessionDurationRef.current = sessionDuration;
  }, [sessionDuration]);

  // Session duration timer
  useEffect(() => {
    if (sessionState === "live" || sessionState === "ai_speaking") {
      if (!timerRef.current) {
        timerRef.current = setInterval(() => {
          setSessionDuration((d) => d + 1);
        }, 1000);
      }
    } else if (!isActive) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {};
  }, [sessionState, isActive]);

  // Auto-stop when countdown reaches 0
  useEffect(() => {
    if (!isActive) return;
    const total = Math.max(0, Math.round(maxSessionMinutes * 60));
    if (total === 0) return;
    const remaining = total - sessionDuration;
    if (remaining <= 0 && !stoppedByTimerRef.current) {
      stoppedByTimerRef.current = true;
      sessionRef.current?.stop();
    }
  }, [isActive, maxSessionMinutes, sessionDuration]);

  // Load last transcript from sessionStorage (survives refresh)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(transcriptStorageKey(agentId));
      if (!raw) {
        setHasSavedTranscript(false);
        return;
      }
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) {
        setHasSavedTranscript(false);
        return;
      }
      const restored = parsed
        .filter((x) => x && typeof x === "object")
        .map((x) => x as TranscriptEntry)
        .filter(
          (x) =>
            (x.role === "user" || x.role === "agent") &&
            typeof x.text === "string" &&
            typeof x.timestamp === "number",
        );
      if (restored.length > 0) {
        transcriptRef.current = restored;
        setTranscript(restored);
        setHasSavedTranscript(true);
      } else {
        setHasSavedTranscript(false);
      }
    } catch {
      setHasSavedTranscript(false);
    }
  }, [agentId]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(MR_NAME_STORAGE_KEY);
      if (saved) setMrName(saved);
    } catch {
      // ignore
    }
  }, []);

  const persistMrName = useCallback((value: string) => {
    setMrName(value);
    try {
      localStorage.setItem(MR_NAME_STORAGE_KEY, value);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(onboardingStorageKey(agentId));
      setShowOnboarding(!seen);
    } catch {
      setShowOnboarding(true);
    }
  }, [agentId]);

  const completeOnboarding = useCallback(() => {
    try {
      localStorage.setItem(onboardingStorageKey(agentId), "1");
    } catch {
      // ignore
    }
    setShowOnboarding(false);
  }, [agentId]);

  const runAnalysis = useCallback(async (entries: TranscriptEntry[]) => {
    if (entries.length < 2) return;
    setAnalysisLoading(true);
    setAnalysisError(null);
    setAnalysis(null);
    setSessionSaveNote(null);
    try {
      const resp = await fetch(`${getBackendHttpBase()}/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agent_id: agentId, transcript: entries }),
      });
      const data = await resp.json() as AnalysisResult & { error?: string };
      if (data.error) {
        setAnalysisError(data.error);
      } else {
        setAnalysis(data);
        const trimmedMr = mrName.trim();
        if (trimmedMr) {
          try {
            await saveTrainingSession({
              agent_id: agentId,
              mr_name: trimmedMr,
              transcript: entries,
              report: data,
              hcp_name: profile.name,
              started_at: sessionStartedAtRef.current ?? undefined,
              duration_seconds:
                sessionDurationRef.current > 0
                  ? sessionDurationRef.current
                  : undefined,
            });
            setSessionSaveNote("Seja shranjena za coach pregled.");
          } catch (saveErr) {
            setSessionSaveNote(
              `Analiza OK, shranjevanje seje ni uspelo: ${
                saveErr instanceof Error ? saveErr.message : String(saveErr)
              }`,
            );
          }
        } else {
          setSessionSaveNote(
            "Vnesi ime MR pred sejo, da se poročilo shrani za coach.",
          );
        }
      }
    } catch (err) {
      setAnalysisError(`Analiza ni uspela: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setAnalysisLoading(false);
    }
  }, [agentId, mrName, profile.name]);

  const analyzeLastTranscript = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(transcriptStorageKey(agentId));
      if (!raw) {
        setAnalysisError("Ni shranjenega transkripta za analizo.");
        return;
      }
      const parsed = JSON.parse(raw) as TranscriptEntry[];
      if (!Array.isArray(parsed) || parsed.length < 2) {
        setAnalysisError("Shranjeni transkript je prekratek za analizo.");
        return;
      }
      transcriptRef.current = parsed;
      setTranscript(parsed);
      runAnalysis(parsed);
    } catch {
      setAnalysisError("Shranjeni transkript je poškodovan ali neberljiv.");
    }
  }, [agentId, runAnalysis]);

  const handleStart = useCallback(async () => {
    completeOnboarding();
    setErrorMsg(null);
    setTranscript([]);
    setSessionDuration(0);
    stoppedByTimerRef.current = false;
    setAnalysis(null);
    setAnalysisError(null);
    setAnalysisLoading(false);
    setSessionJustEnded(false);
    setSessionSaveNote(null);
    sessionStartedAtRef.current = Date.now() / 1000;
    transcriptRef.current = [];
    try {
      sessionStorage.removeItem(transcriptStorageKey(agentId));
    } catch {
      // ignore
    }
    setHasSavedTranscript(false);
    lastTranscriptMergeRef.current = null;

    const session = new GeminiSession(agentId, {
      onStateChange: (s) => {
        setSessionState(s);
        if (s === "ended") {
          setSessionJustEnded(true);
          runAnalysis(transcriptRef.current);
        }
      },
      onTranscript: (entry) =>
        setTranscript((prev) => {
          const text = (entry.text ?? "").trim();
          if (!text) return prev;

          const now = Date.now();
          const last = prev[prev.length - 1];
          const canMerge =
            !!last &&
            last.role === entry.role &&
            (() => {
              const meta = lastTranscriptMergeRef.current;
              if (!meta) return false;
              if (meta.role !== entry.role) return false;
              return now - meta.atMs <= 1500;
            })();

          lastTranscriptMergeRef.current = { role: entry.role, atMs: now };

          const next = canMerge
            ? [
                ...prev.slice(0, -1),
                {
                  ...last,
                  text:
                    (last.text ?? "").trim().length > 0
                      ? `${(last.text ?? "").trim()} ${text}`
                      : text,
                },
              ]
            : [...prev, { ...entry, text }];

          transcriptRef.current = next;
          try {
            sessionStorage.setItem(transcriptStorageKey(agentId), JSON.stringify(next));
            setHasSavedTranscript(true);
          } catch {
            // ignore
          }
          return next;
        }),
      onInputRms: (rms) => setInputRms(rms),
      onOutputRms: (rms) => setOutputRms(rms),
      onError: (msg) => setErrorMsg(msg),
    });

    sessionRef.current = session;

    try {
      await session.start();
    } catch (err) {
      setErrorMsg(`Napaka pri zagonu: ${err instanceof Error ? err.message : String(err)}`);
      setSessionState("error");
    }
  }, [agentId, completeOnboarding, runAnalysis]);

  const handleStop = useCallback(() => {
    sessionRef.current?.stop();
    setInputRms(0);
    setOutputRms(0);
    setSessionJustEnded(true);
    runAnalysis(transcriptRef.current);
  }, [runAnalysis]);

  const handleStartFromOnboarding = useCallback(() => {
    completeOnboarding();
    void handleStart();
  }, [completeOnboarding, handleStart]);

  const handleStartNextSession = useCallback(() => {
    setAnalysis(null);
    setAnalysisError(null);
    setSessionJustEnded(false);
    setSessionSaveNote(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    void handleStart();
  }, [handleStart]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      sessionRef.current?.destroy();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const formatDuration = (secs: number) => {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const formatRemaining = (elapsedSecs: number) => {
    const total = Math.max(0, Math.round(maxSessionMinutes * 60));
    if (total <= 0) return "∞";
    const remaining = Math.max(0, total - elapsedSecs);
    return formatDuration(remaining);
  };

  const objectionBadge = OBJECTION_BADGE[profile.objection_level] ?? OBJECTION_BADGE.medium;

  const activeRms = isAiSpeaking ? outputRms : inputRms;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-start p-6 gap-6">
      {/* Header */}
      <header className="w-full max-w-3xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center font-bold text-sm">
            AI
          </div>
          <span className="font-semibold text-slate-200 text-lg">
            FarmaVoice — MR trening
          </span>
        </div>
        <div className="flex items-center gap-3">
          {sessionState === "idle" && !showOnboarding && (
            <button
              type="button"
              onClick={() => setShowOnboarding(true)}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded px-2 py-1"
            >
              Navodila
            </button>
          )}
        {isActive && (() => {
          const totalSecs = Math.max(0, Math.round(maxSessionMinutes * 60));
          const remainingSecs =
            totalSecs > 0 ? Math.max(0, totalSecs - sessionDuration) : null;
          const urgency =
            remainingSecs != null && remainingSecs <= 60
              ? "text-red-400"
              : remainingSecs != null && remainingSecs <= 120
                ? "text-amber-400"
                : "text-slate-300";
          return (
            <div className="flex flex-col items-end gap-0.5">
              <div className={`font-mono text-sm tabular-nums ${urgency}`}>
                {formatRemaining(sessionDuration)}
              </div>
              <div className="text-[0.7rem] text-slate-600">
                {remainingSecs != null && remainingSecs <= 120
                  ? "obisk se izteka"
                  : "preostali čas"}
              </div>
            </div>
          );
        })()}
        </div>
      </header>

      {showOnboarding && sessionState === "idle" && (
        <SessionOnboarding
          profileName={profile.name}
          profileRole={profile.role}
          productName={productName}
          maxSessionMinutes={maxSessionMinutes}
          onStart={handleStartFromOnboarding}
          onDismiss={completeOnboarding}
        />
      )}

      {/* MR identity (coach tracking) */}
      <section
        className="w-full max-w-3xl rounded-2xl bg-slate-900 border border-slate-800 p-4"
        aria-label="Identiteta prodajalca"
      >
        <label htmlFor="mr-name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Ime MR (za coach)
        </label>
        <input
          id="mr-name"
          type="text"
          value={mrName}
          onChange={(e) => persistMrName(e.target.value)}
          disabled={isActive}
          placeholder="npr. Ana Kovač"
          autoComplete="name"
          className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-60"
        />
        <p className="mt-1.5 text-[0.65rem] text-slate-600">
          Po analizi se seja shrani na strežnik za pregled v Coach pogledu.
        </p>
      </section>

      {/* Agent profile card */}
      <section
        className="w-full max-w-3xl rounded-2xl bg-slate-900 border border-slate-800 p-5"
        aria-label="Profil agenta"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-bold text-white">{profile.name}</h1>
            <p className="text-sm text-slate-400">{profile.role}</p>
            <p className="text-sm text-slate-500 mt-1 max-w-xl leading-relaxed">
              {profile.character}
            </p>
          </div>
          <span
            className={`shrink-0 mt-1 rounded-full px-3 py-1 text-xs font-semibold ${objectionBadge.cls}`}
          >
            Ugovori: {objectionBadge.label}
          </span>
        </div>
      </section>

      {/* Main interaction area */}
      <div className="w-full max-w-3xl flex flex-col lg:flex-row gap-6 flex-1">
        {/* Left: Visualizer + controls */}
        <div className="flex flex-col items-center gap-6 lg:w-72 shrink-0">
          <AudioVisualizer
            rms={activeRms}
            isAiSpeaking={isAiSpeaking}
            isListening={isListening}
            size={180}
          />

          {/* Status label */}
          <div className="flex flex-col items-center gap-1 text-center min-h-[3rem]">
            <span
              className={`text-sm font-medium transition-colors duration-300 ${
                STATE_COLORS[sessionState]
              }`}
            >
              {STATE_LABELS[sessionState]}
            </span>
            {errorMsg && (
              <span className="text-xs text-red-400 max-w-[16rem] leading-snug">
                {errorMsg}
              </span>
            )}
          </div>

          {/* Start / Stop button */}
          <button
            onClick={isActive ? handleStop : handleStart}
            disabled={
              sessionState === "loading_docs" ||
              sessionState === "connecting" ||
              sessionState === "reconnecting"
            }
            aria-label={isActive ? "Ustavi sejo" : "Začni sejo"}
            className={`w-full rounded-xl py-3 px-6 font-semibold text-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:cursor-not-allowed disabled:opacity-50
              ${
                isActive
                  ? "bg-red-600 hover:bg-red-500 text-white focus-visible:ring-red-500"
                  : "bg-blue-600 hover:bg-blue-500 text-white focus-visible:ring-blue-500"
              }`}
          >
            {isActive ? "Ustavi sejo" : "Začni trening"}
          </button>

          {/* Analyze last transcript (survives refresh) */}
          {!isActive && hasSavedTranscript && (
            <button
              onClick={analyzeLastTranscript}
              disabled={analysisLoading}
              className="w-full rounded-xl py-2.5 px-6 font-semibold text-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:cursor-not-allowed disabled:opacity-50 bg-slate-800 hover:bg-slate-700 text-slate-200 focus-visible:ring-slate-500"
            >
              Analiziraj zadnji transkript
            </button>
          )}

          {/* Mic / AI speaking indicator row */}
          {isActive && (
            <div className="flex gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1.5">
                <span
                  className={`w-2 h-2 rounded-full ${
                    isListening ? "bg-emerald-400 animate-pulse" : "bg-slate-700"
                  }`}
                />
                Mikrofon
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className={`w-2 h-2 rounded-full ${
                    isAiSpeaking ? "bg-blue-400 animate-pulse" : "bg-slate-700"
                  }`}
                />
                Agent
              </span>
            </div>
          )}
        </div>

        {/* Right: Transcript */}
        <div
          className="flex-1 rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden"
          style={{ minHeight: "24rem", maxHeight: "32rem" }}
          aria-label="Transkript pogovora"
          aria-live="polite"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <span className="text-sm font-medium text-slate-300">Transkript</span>
            {transcript.length > 0 && (
              <button
                onClick={() => {
                  setTranscript([]);
                  transcriptRef.current = [];
                  try {
                    sessionStorage.removeItem(transcriptStorageKey(agentId));
                  } catch {
                    // ignore
                  }
                  setHasSavedTranscript(false);
                }}
                className="text-xs text-slate-600 hover:text-slate-400 transition-colors"
              >
                Počisti
              </button>
            )}
          </div>
          <div className="p-4 h-[calc(100%-3rem)] overflow-y-auto">
            <TranscriptDisplay entries={transcript} agentName={profile.name} />
          </div>
        </div>
      </div>

      {/* Post-session analysis */}
      {sessionSaveNote && !analysisLoading && (
        <p
          className={`text-xs text-center max-w-md ${
            sessionSaveNote.includes("shranjena") ? "text-emerald-500" : "text-amber-500"
          }`}
          role="status"
        >
          {sessionSaveNote}
        </p>
      )}

      <div ref={analysisRef}>
        {(analysisLoading || analysis || analysisError) && (
          <SessionAnalysis
            result={analysis!}
            isLoading={analysisLoading}
            error={analysisError}
            agentId={agentId}
            sessionEnded={sessionJustEnded}
            onStartNextSession={
              !isActive && !analysisLoading ? handleStartNextSession : undefined
            }
          />
        )}
      </div>

      {/* Footer hint */}
      <p className="text-xs text-slate-500 text-center max-w-md leading-relaxed">
        Simulacija obiska pri zdravstvenem delavcu (HCP) za medicinskega
        predstavnika. Vsebina temelji na uradnih SmPC virih — ni namenjena
        pacientom ali medicinskemu svetovanju.
      </p>
    </div>
  );
}
